import * as Sentry from '@sentry/react';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { isPublicAuthOptionalPath } from '@/route/publicRoutePaths';
import { SignOutUser } from '../store/Auth';
import { store } from '../store/index';

/**
 * Setup axios interceptors for authentication and error handling
 *
 * - 401 Unauthorized: Clears auth state and redirects to login
 * - 403 Forbidden: Handled by route guards
 */

// Track if we're already handling a 401 to prevent multiple sign_out calls
let isHandling401 = false;
const X_SUBDOMAIN_STORAGE_KEY = 'xSubdomain';
type TimedAxiosConfig = InternalAxiosRequestConfig & { _sentryStartTime?: number };

function getStoredTenantSubdomain(): string {
  if (typeof localStorage === 'undefined') {
    return '';
  }

  return localStorage.getItem(X_SUBDOMAIN_STORAGE_KEY) || '';
}

export function getSelectedTenantSubdomain(): string {
  const auth = store.getState().auth;
  return auth.data.account || auth.currentDomain || getStoredTenantSubdomain();
}

export function applySelectedTenantSubdomain(subdomain: string): void {
  if (!subdomain) {
    return;
  }

  axios.defaults.headers.common['x-subdomain'] = subdomain;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(X_SUBDOMAIN_STORAGE_KEY, subdomain);
  }
}

export function setupAxiosInterceptors(instance: AxiosInstance = axios): void {
  // Record request start time for response time tracking
  instance.interceptors.request.use((config) => {
    const timedConfig = config as TimedAxiosConfig;
    timedConfig._sentryStartTime = Date.now();
    const selectedSubdomain = getSelectedTenantSubdomain();

    if (selectedSubdomain) {
      config.headers = config.headers || {};
      config.headers['x-subdomain'] = selectedSubdomain;
    }

    return config;
  });

  // Response interceptor for 401 Unauthorized
  instance.interceptors.response.use(
    (response) => {
      const duration = Date.now() - ((response.config as TimedAxiosConfig)._sentryStartTime ?? Date.now());
      const method = response.config.method?.toUpperCase() ?? 'UNKNOWN';

      Sentry.metrics.distribution('api.response_time', duration, {
        unit: 'millisecond',
        attributes: { method, status: String(response.status) },
      });
      Sentry.metrics.count('api.requests', 1, {
        attributes: { method, status_class: `${Math.floor(response.status / 100)}xx` },
      });

      return response;
    },
    async (error) => {
      const { response } = error;

      if (response?.status === 401 && !isHandling401) {
        if (isPublicAuthOptionalPath(window.location.pathname)) {
          return Promise.reject(error);
        }
        isHandling401 = true;

        try {
          // Session expired or unauthorized - clear auth state and redirect to login
          await store.dispatch(SignOutUser());
        } catch (signOutError) {
          // If sign_out fails (session already expired), that's ok
          console.debug('Sign out failed (session likely expired)', signOutError);
        } finally {
          // Redirect to login page
          if (window.location.pathname !== '/auth/sign_in') {
            window.location.href = '/auth/sign_in';
          }

          // Reset flag after a short delay to allow new 401s to be handled
          setTimeout(() => {
            isHandling401 = false;
          }, 1000);
        }

        return Promise.reject(error);
      }

      // No response = network error or CORS block
      if (!response) {
        Sentry.metrics.count('api.errors', 1, { attributes: { error_type: 'network_or_cors' } });
        Sentry.captureException(error, {
          level: 'error',
          tags: { error_type: 'network_or_cors' },
          extra: {
            url: error?.config?.url,
            method: error?.config?.method,
            message: error?.message,
            page: window.location.pathname,
          },
          fingerprint: ['network_or_cors', error?.config?.url ?? 'unknown'],
        });
        return Promise.reject(error);
      }

      // Capture 4xx client errors (login failures, forbidden, validation errors, etc.)
      // and 5xx server errors — excluding 401 which is a handled session-expiry flow
      if (response.status >= 400 && response.status !== 401) {
        const isServerError = response.status >= 500;
        Sentry.metrics.count('api.errors', 1, {
          attributes: {
            error_type: isServerError ? 'server_error' : 'client_error',
            status_code: String(response.status),
          },
        });
        Sentry.captureException(error, {
          level: isServerError ? 'error' : 'warning',
          tags: {
            error_type: isServerError ? 'server_error' : 'client_error',
            status_code: String(response.status),
          },
          extra: {
            url: response.config?.url,
            method: response.config?.method,
            status: response.status,
            response_data: response.data,
            page: window.location.pathname,
          },
          fingerprint: [String(response.status), response.config?.method ?? '', response.config?.url ?? ''],
        });
      }

      // Pass other errors through
      return Promise.reject(error);
    }
  );
}
